/**
 * ファイルダウンロード用のユーティリティ関数
 */

/**
 * MIMEタイプから拡張子へのマッピング
 */
const MIME_TO_EXTENSION: Record<string, string> = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
}

/**
 * MIMEタイプから拡張子を取得する
 * @param mimeType - MIMEタイプ（例: "image/jpeg"）
 * @param fallback - 不明なMIMEタイプの場合のフォールバック値
 * @returns 拡張子（ドット付き、例: ".jpg"）
 */
export const getExtensionFromMimeType = (mimeType: string, fallback = '.jpg'): string => {
  return MIME_TO_EXTENSION[mimeType] || fallback
}

/**
 * ダウンロードファイルのオプション
 */
interface DownloadFileOptions {
  /** 拡張子がない場合にblob.typeから自動的に付与するか */
  autoExtension?: boolean
}

/**
 * 外部画像URLをCORS Proxy経由のURLに変換する
 * 外部オリジンの画像URLは、<a>タグのdownload属性や<img>タグのsrcで動作しない
 * company-workerのcorsproxyを経由することで、同一オリジンからのアクセスにする
 * ローカル環境（development）では元のURLをそのまま返す
 * @param url - 元のURL
 * @returns CORS Proxy経由のURL、またはローカル環境の場合は元のURL
 */
export const convertToCorsProxyUrl = (url: string): string => {
  if (!url) {
    return url
  }
  const trimmedUrl = url.trim()

  // ローカル環境では元のURLをそのまま返す
  if (import.meta.env.MODE === 'development') {
    return trimmedUrl
  }

  // 既にCORS proxy経由のURLの場合はそのまま返す
  if (trimmedUrl.startsWith('/corsproxy')) {
    return trimmedUrl
  }

  // 相対パスの場合はそのまま返す（同一オリジン）
  if (trimmedUrl.startsWith('/')) {
    return trimmedUrl
  }

  // 外部URL（http:// or https://）の場合はCORS proxy経由にする
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return `/corsproxy?url=${encodeURIComponent(trimmedUrl)}`
  }

  return trimmedUrl
}

/**
 * 指定されたURLのファイルをダウンロードする
 * R2のURLの場合は自動的にCORS Proxy経由でダウンロードする
 * @param url - ダウンロードするファイルのURL
 * @param filename - 保存時のファイル名
 * @param options - ダウンロードオプション
 */
export const downloadFile = async (
  url: string,
  filename: string,
  options?: DownloadFileOptions,
): Promise<void> => {
  const proxyUrl = convertToCorsProxyUrl(url)

  try {
    const response = await fetch(proxyUrl, { credentials: 'include' })
    if (!response.ok) {
      throw new Error(`Failed to fetch file. status=${response.status}`)
    }

    const blob = await response.blob()
    if (!blob.size || blob.type.includes('text') || blob.type.includes('html')) {
      throw new Error('Downloaded content is not a valid binary file')
    }

    // 拡張子自動付与
    let finalFilename = filename
    if (options?.autoExtension && !filename.match(/\.[a-z0-9]+$/i)) {
      const extension = getExtensionFromMimeType(blob.type)
      finalFilename = `${filename}${extension}`
    }

    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = finalFilename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('[downloadFile] Failed to download file', error)
    throw error
  }
}
